import React from "react";
import "./FanAvatar.css";
import { initialsOf } from "../utils/initials";

// The fan identity bubble (fan accounts; grey rework, founder — "as
// generic as possible", supersedes the r6 purple gradient): light-grey
// circle, iOS add-contact style. Brand-new fans get the person silhouette
// with a small "+" INSIDE the icon (the Create-Profile nudge — the old
// floating green badge is gone), their initials once they have a name (two
// for a two-word name, 1.5.2), their photo once signed in with one — a photo
// always wins over the letters.
//
// Size comes from the WRAPPER class the caller passes (fanchip-avatar 34px,
// fps-avatar 30px, fps-ava-big 76px) — this component only decides what's
// inside the circle.

const SILHOUETTE_PATH =
  "M12 12.2a4.1 4.1 0 1 0 0-8.2 4.1 4.1 0 0 0 0 8.2Zm0 2c-4.1 0-7.4 2.3-7.4 5.1 0 .5.4.9.9.9h13c.5 0 .9-.4.9-.9 0-2.8-3.3-5.1-7.4-5.1Z";

const FanAvatar: React.FC<{
  className: string;
  /** Display name — its initials become the letters (utils/initials). Empty → silhouette. */
  name?: string;
  /** Account photo — wins over letter/silhouette. */
  avatarUrl?: string | null;
  /** Create-Profile nudge: draws the "+" inside the silhouette icon
      (never for signed-in fans). */
  showBadge?: boolean;
  /** Silhouette glyph size in px (scaled to the wrapper's bubble size). */
  silhouetteSize?: number;
  children?: React.ReactNode;
}> = ({
  className,
  name = "",
  avatarUrl = null,
  showBadge = false,
  silhouetteSize = 18,
  children,
}) => {
  const initial = initialsOf(name);
  const photo = !!avatarUrl;
  return (
    <span
      className={`${className} fanava${photo ? " fanava--photo" : ""}`}
      aria-hidden="true"
    >
      {photo ? (
        // no-referrer: Google avatar URLs 403 when a referrer rides along.
        <img src={avatarUrl as string} alt="" referrerPolicy="no-referrer" />
      ) : initial ? (
        <span className={`fanava-initials${Array.from(initial).length > 1 ? " fanava-initials--two" : ""}`}>{initial}</span>
      ) : (
        <svg
          width={silhouetteSize}
          height={silhouetteSize}
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          {/* Nudge state: person nudged right, "+" at its upper-left — the
              iOS add-contact composition the founder picked (8/26). */}
          <path
            d={SILHOUETTE_PATH}
            transform={showBadge ? "translate(1.7 0.7) scale(0.93)" : undefined}
          />
          {showBadge && (
            <g stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" fill="none">
              <line x1="4.5" y1="2.5" x2="4.5" y2="7.9" />
              <line x1="1.8" y1="5.2" x2="7.2" y2="5.2" />
            </g>
          )}
        </svg>
      )}
      {children}
    </span>
  );
};

export default FanAvatar;
